import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  EIconName,
  NumberInput,
  SwitchElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Components
import {
  DeleteCostPerSessionModal
} from '../../../../modals/delete-cost-per-session-modal/delete-cost-per-session-modal';

// Styles
import './price-per-message.scss';

// Thunks
import { updatePricePerMessage } from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

// Models
import { ICounsellor, IFreeMinutes } from '../../../../interfaces/counsellors.interfaces';
import { ICurrency } from '../../../../interfaces/company.interfaces';

// Utils
import { numToFixed } from '../../../../utils/math';
import { replaceCurrencySymbol } from '../../../../utils/currency';

type TFormValue = {
  rate: string;
  profit: string;
};

export type TPricePerMessageRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TPricePerMessageProps = {
  counsellor: ICounsellor;
  free_minutes: IFreeMinutes;
  is_stripe_connected: boolean;
  percentFee: number;
  transactionFee: number;
  currency: ICurrency;
};

export const PricePerMessage = forwardRef<TPricePerMessageRef, TPricePerMessageProps>((
    { counsellor, free_minutes, is_stripe_connected, percentFee, transactionFee, currency }: TPricePerMessageProps, ref
  ) => {
    const [form] = Form.useForm<TFormValue>();
    const rate = Form.useWatch('rate', { form, preserve: true });

    const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

    const getValueByProfit = (profitValue: number): number => {
      return numToFixed(((profitValue) / (1 - (percentFee / 100))) + transactionFee);
    }

    const getValueByRate = (currValue: number): number => {
      return numToFixed(currValue - transactionFee - getServiceFeeValue(currValue));
    }

    const getProfitInitialValue = (currValue: number): string => {
      const fee = percentFee / 100;
      if (currValue * fee <= 0) return `${ currency.symbol }0`;
      // return numToFixed(rateValue * fee);
      return numToFixed(getValueByRate(currValue)).toString();
    }

    const getServiceFeeValue = (rateValue: number): number => {
      const fee = percentFee / 100;
      if (rateValue * fee <= 0 || numToFixed(rateValue * fee) <= 0) return 0.01;
      return numToFixed(((rateValue - transactionFee) * fee));
    }

    const [savingChanges, setSavingChanges] = useState<boolean>(false);
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [availableMessages, setAvailableMessages] = useState<boolean>(counsellor.is_messages_enabled);

    const dispatch = useDispatch<any>();

    const full_free_service_enabled = !!free_minutes?.full_free_service_enabled;

    const parserHandler = (value: string | undefined): number => {
      return Number.parseFloat(replaceCurrencySymbol(value || '', currency.symbol));
    }

    const getNumberField = (value: string | number): number => {
      return Number.parseFloat(replaceCurrencySymbol(`${ value }`, currency.symbol));
    }

    const rateInitialValue = `${ currency.symbol }${ counsellor.message_rate.value || 0.01 }`;
    const serviceFee = useMemo(() => numToFixed(getServiceFeeValue(getNumberField(rate)) + transactionFee), [rate]);
    const profitInitialValue = getProfitInitialValue(counsellor.message_rate.value);

    const toggleMessageRates = async (): Promise<void> => {
      setSavingChanges(true);
      const { payload } = await dispatch(updatePricePerMessage({
        worker_id: counsellor.id,
        is_message_rate_enabled: !availableMessages,
      }));
      setSavingChanges(false);

      if (!payload) return;
      setHasChanges(false);
    }

    const lastRatesDeleteHandler = (state: boolean): void => {
      setDeleteOpen(false);
      if (!state) return;
      toggleMessageRates();
      setAvailableMessages(!availableMessages);
    }

    const toggleEditMode = (checked: boolean, event: any): void => {
      if (!is_stripe_connected && checked) {
        event.preventDefault();
        dispatch(openNotification({
          type: 'error',
          description: 'Please connect your Stripe account first to deactivate the free services'
        }));
        return;
      }
      if (!checked && counsellor.rates_enabled_count === 1) {
        setDeleteOpen(true);
        event.preventDefault();
        return;
      }
      if (counsellor.is_messages_enabled && hasChanges) {
        form.setFieldsValue({
          rate: `${ counsellor.message_rate.value || 0.01 }`,
          profit: '0'
        });
      }

      toggleMessageRates();
      setAvailableMessages(!availableMessages);
    }

    const handleRateChange = (
      currentValue: string | number,
      profitField: keyof TFormValue,
      currentField: keyof TFormValue
    ): void => {
      const profitValue = currentField.includes('profit') ? getValueByProfit(+currentValue) : getValueByRate(+currentValue);
      const savedValue = getNumberField(counsellor.message_rate.value);

      const hasChanges =
        currentField.includes('profit') ? savedValue !== numToFixed(profitValue) :
          savedValue !== numToFixed(profitValue) + getServiceFeeValue(savedValue);

      form.setFieldValue(profitField, (!profitValue || profitValue < 0) ? 0 : numToFixed(profitValue));
      setHasChanges(hasChanges);
    };

    const onValuesChange = (
      currentValue: { [key in keyof TFormValue]: string | number },
      _: TFormValue
    ): void => {
      if (currentValue?.hasOwnProperty('rate')) {
        handleRateChange(currentValue?.rate, 'profit', 'rate');
      }

      if (currentValue?.hasOwnProperty('profit')) {
        handleRateChange(currentValue?.profit, 'rate', 'profit');
      }
    };

    const saveChanges = async (): Promise<void> => {
      const { profit } = form.getFieldsValue();
      setSavingChanges(true);

      const { payload } = await dispatch(updatePricePerMessage({
        worker_id: counsellor.id,
        message_rate: Number(replaceCurrencySymbol(`${ profit }`, currency.symbol)) || 0.01,
        is_message_rate_enabled: counsellor.is_messages_enabled,
      }));
      setSavingChanges(false);

      if (!payload) return;
      setHasChanges(false);
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.setFieldsValue({
          profit: profitInitialValue,
          rate: replaceCurrencySymbol(rateInitialValue, currency.symbol)
        });
        setHasChanges(false);
        setSavingChanges(false);
      }
    }));

    useEffect(() => {
      setAvailableMessages(counsellor.is_messages_enabled);
    }, [counsellor]);

    return (
      <div className={ `price-per-message ${ free_minutes.full_free_service_enabled ? 'disabled-block' : '' }` }>
        <DeleteCostPerSessionModal open={ deleteOpen }
                                   isLastItem={ counsellor?.rates_enabled_count === 1 }
                                   title={ 'Are you sure you want to delete Request and Cost?' }
                                   onClose={ lastRatesDeleteHandler }/>

        <div className="price-per-message--description">
          In this section, you can set the price for text-based consultations using Text-Based Requests. To enable such
          consultations, activate the option utilizing the provided switcher. The switcher's off-state indicates that this
          type of consultation is not available in the Consultant's profile for Clients.
        </div>

        <div className="price-per-message--description">
          Please note: <br/>
          Clients interested in using this feature to get a paid consultation in such a manner are informed on their User
          Interface that they should provide a comprehensive and detailed description of their request to ensure the most
          effective response from the Consultant. Additionally, Clients are made aware that they will be charged for each
          request they submit. This ensures a clear Consultant-Client interaction and understanding of the service and its
          charges for Clients.
        </div>

        <div className="price-per-message--description">
          When setting a Service Price, consider the service charge you wish to receive as a business. The system assists
          you by showing the Credit Card Processing Fee in relation to your set service price. No hidden or extra charges.
          This enables transparency in pricing for you.
        </div>

        {/*<div className="price-per-minute--notification">*/ }
        {/*  <CustomIcon name={ EIconName.Info } skipSize={ true }/> Each Minute-Based Call is subject to a*/ }
        {/*  fixed { currency.symbol }{ transactionFee } { currency.name } transaction fee charged by the Payment System,*/ }
        {/*  regardless of call length. This fee is not included in the*/ }
        {/*  Payable Amount for Clients per min.*/ }
        {/*</div>*/ }

        <Form className="price-per-message--edit-price"
              form={ form }
              onValuesChange={ onValuesChange }>
          <div className="edit-price-col edit-price-col--type">
            <div className="col-header"/>
            <div className="col-body">
              <CustomIcon name={ EIconName.Message } size={ 'size-xll' }/> <span
              className="col-text">Correspondence</span>
              <SwitchElement
                disabled={ full_free_service_enabled }
                checked={ availableMessages }
                onChange={ toggleEditMode }
              />
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header">
              Payable Amount for Clients per req <CustomTooltip
              title={ 'The amount covers both the Service Price and the Credit Card Processing Fee, with no hidden or extra charges' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'rate' } initialValue={ rateInitialValue }>
                <NumberInput
                  disabled={ (!counsellor.is_messages_enabled && !availableMessages) || savingChanges || full_free_service_enabled }
                  min={ transactionFee + 0.21 }
                  max={ 10000 }
                  placeholder={ 'Rate' }
                  precision={ 2 }
                  formatter={ (value) => `${ currency.symbol }${ value }` }
                  parser={ parserHandler }
                  controls={ false }/>
              </Form.Item>
              /rqst
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header"/>
            <div className="col-body col-body--equals">
              =
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header">
              Credit Card Processing Fees <CustomTooltip
              title={ 'Fee charged by the Payment System for processing credit card transactions' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              { currency.symbol }{ serviceFee } /rqst
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header"/>
            <div className="col-body col-body--plus">
              +
            </div>
          </div>

          <div className="edit-price-col">
            <div className="col-header">
              Service Price <CustomTooltip
              title={ 'The amount to be credited into your bank account for provided service to your Clients' }><CustomIcon
              name={ EIconName.Info }
              size={ 'size-md' }/></CustomTooltip>
            </div>
            <div className="col-body">
              <Form.Item name={ 'profit' } initialValue={ profitInitialValue }>
                <NumberInput
                  disabled={ (!counsellor.is_messages_enabled && !availableMessages) || savingChanges || full_free_service_enabled }
                  placeholder={ 'Profit' }
                  min={ 0.2 }
                  max={ 10000 }
                  precision={ 2 }
                  formatter={ (value) => `${ currency.symbol }${ value }` }
                  parser={ parserHandler }
                  controls={ false }/>
              </Form.Item>
              /rqst
            </div>
          </div>

          <div className="edit-price-col edit-price-col--action">
            <div className="col-header"/>
            <div className="col-body" hidden={ !hasChanges }>
              { availableMessages && <CustomButton disabled={ savingChanges }
                                                   loading={ savingChanges }
                                                   size={ 'small' }
                                                   text={ 'Save' }
                                                   onClick={ saveChanges }/> }
            </div>
          </div>
        </Form>
      </div>
    );
  }
)
